import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import useApiCall from "../common/api-call";
import PlanGraphViz from "./PlanGraphViz";

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`thread-tabpanel-${index}`}
      aria-labelledby={`thread-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const HISTORY_STORAGE_KEY = "debug_automation_history";

const Debug = () => {
  const [name, setName] = useState("");
  const [tenant, setTenant] = useState("");
  const [scope, setScope] = useState("");
  const [automation, setAutomation] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const { apiCall } = useApiCall();

  // Load history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
      } catch (e) {
        console.error("Error parsing history from localStorage:", e);
        setHistory([]);
      }
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const saveToHistory = () => {
    // Create a new history entry
    const newEntry = {
      id: Date.now().toString(), // Use timestamp as unique ID
      name: name || `${tenant}-${scope}-${automation}`, // Use name if provided, otherwise create a default name
      tenant,
      scope,
      automation,
      timestamp: new Date().toISOString(),
    };

    // Check if this combination already exists to avoid duplicates
    const entryExists = history.some(
      item =>
        item.tenant === tenant &&
        item.scope === scope &&
        item.automation === automation,
    );

    if (!entryExists) {
      // Add to history, limiting to most recent 10 entries
      const updatedHistory = [newEntry, ...history].slice(0, 10);
      setHistory(updatedHistory);

      // Save to localStorage
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
    }
  };

  const loadFromHistory = historyItem => {
    setName(historyItem.name || "");
    setTenant(historyItem.tenant);
    setScope(historyItem.scope);
    setAutomation(historyItem.automation);
    setHistoryDialogOpen(false);
  };

  const removeHistoryItem = (e, id) => {
    e.stopPropagation(); // Prevent triggering the parent click handler
    const updatedHistory = history.filter(item => item.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  const handleDebug = async () => {
    setLoading(true);
    setResult(null);

    try {
      const endpoint = `/automation/debug?tenant=${encodeURIComponent(
        tenant,
      )}&scope=${encodeURIComponent(scope)}&id=${encodeURIComponent(
        automation,
      )}`;
      const response = await apiCall(endpoint, "GET", null, "admin");

      // Save successful queries to history
      saveToHistory();

      setResult(response);
      // Reset to first tab when new data arrives
      setActiveTab(0);
    } catch (error) {
      console.error("Error debugging automation:", error);
      setResult({ error: "Failed to debug automation" });
    } finally {
      setLoading(false);
    }
  };

  // Show GraphViz visualization
  const showGraphVisualization = () => {
    setGraphDialogOpen(true);
  };

  // Generate DOT data from the current thread's plan
  const generateDotData = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      if (currentThread && currentThread.plan) {
        showGraphVisualization();
      }
    }
  };

  // Format relative time for history items
  const formatRelativeTime = timestamp => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const visibleHistory = history.slice(0, 3);
  const hasMoreHistory = history.length > 3;

  const getCurrentThreadPlan = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      return currentThread.plan || "";
    }
    return "";
  };

  const getCurrentThreadMemory = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      return currentThread.memory || "";
    }
    return "";
  };

  return (
    <Box sx={{ padding: 3 }}>
      {history.length > 0 && (
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
            Recent Queries
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", gap: 1, alignItems: "center" }}
          >
            {visibleHistory.map(item => (
              <Chip
                key={item.id}
                label={
                  item.name ||
                  `${item.tenant} / ${item.scope} / ${item.automation}`
                }
                onClick={() => loadFromHistory(item)}
                onDelete={e => removeHistoryItem(e, item.id)}
                deleteIcon={
                  <Tooltip title="Remove from history">
                    <DeleteIcon fontSize="small" />
                  </Tooltip>
                }
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
            {hasMoreHistory && (
              <Chip
                icon={<MoreHorizIcon />}
                label="More"
                onClick={() => setHistoryDialogOpen(true)}
                variant="outlined"
                sx={{ mb: 1 }}
              />
            )}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ padding: 2, marginBottom: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={e => setName(e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
              placeholder="Optional identifier for this debug query"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Tenant ID"
              value={tenant}
              onChange={e => setTenant(e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Scope ID"
              value={scope}
              onChange={e => setScope(e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Automation ID"
              value={automation}
              onChange={e => setAutomation(e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleDebug}
              disabled={!tenant || !scope || !automation || loading}
              sx={{ mt: 2 }}
            >
              {loading ? "Processing..." : "Debug"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {result && result.threads && result.threads.length > 0 && (
        <Paper sx={{ marginBottom: 3 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flexGrow: 1 }}
            >
              {result.threads.map((thread, index) => (
                <Tab
                  key={index}
                  label={`Thread ${thread.threadId}`}
                  id={`thread-tab-${index}`}
                />
              ))}
            </Tabs>

            <Box sx={{ display: "flex", mr: 2 }}>
              <Tooltip title="View Graph Visualization">
                <IconButton
                  color="primary"
                  onClick={showGraphVisualization}
                  size="small"
                >
                  <ViewInArIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {result.threads.map((thread, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Plan
                      </Typography>
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          overflow: "auto",
                          maxHeight: "400px",
                        }}
                      >
                        {thread.plan || "No plan available"}
                      </pre>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Memory
                      </Typography>
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          overflow: "auto",
                          maxHeight: "400px",
                        }}
                      >
                        {thread.memory || "No memory available"}
                      </pre>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          ))}
        </Paper>
      )}

      {result && (!result.threads || result.threads.length === 0) && (
        <Paper sx={{ padding: 3 }}>
          <Typography variant="h6" gutterBottom>
            Debug Result
          </Typography>
          <pre style={{ overflow: "auto", maxHeight: "400px" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Paper>
      )}

      {/* History Selection Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <HistoryIcon sx={{ mr: 1 }} />
            Debugging History
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {history.map(item => (
              <ListItem
                key={item.id}
                button
                onClick={() => loadFromHistory(item)}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={e => {
                      e.stopPropagation();
                      removeHistoryItem(e, item.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    item.name ||
                    `${item.tenant} / ${item.scope} / ${item.automation}`
                  }
                  secondary={`Tenant: ${item.tenant} | Scope: ${
                    item.scope
                  } | Automation: ${item.automation} | ${formatRelativeTime(
                    item.timestamp,
                  )}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* GraphViz Visualization Dialog */}
      <Dialog
        open={graphDialogOpen}
        onClose={() => setGraphDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ViewInArIcon sx={{ mr: 1 }} />
              Plan Visualization
            </Box>
            <Button onClick={() => setGraphDialogOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: "calc(100vh - 64px)", p: 0 }}>
          <PlanGraphViz
            planData={getCurrentThreadPlan()}
            memData={getCurrentThreadMemory()}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Debug;
